import React, { useEffect, useState } from "react";
import axios from "axios";
import { parseCustomDate, formatDateTime } from "../utils/helpers";

const formatWaitDuration = (assignTimeStr) => {
    if (!assignTimeStr) return null;
    const parsed = parseCustomDate(assignTimeStr) || new Date(assignTimeStr);
    if (!parsed || isNaN(parsed.getTime())) return null;

    const now = new Date();
    const diffMs = now.getTime() - parsed.getTime();
    if (diffMs < 0 || diffMs < 60000) return "Just now";

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const days = Math.floor(hours / 24);

    if (days > 0) {
        const remHours = hours % 24;
        return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
    }
    if (hours > 0) {
        return minutes > 0 ? `${hours} hr ${minutes} mins` : `${hours} hr`;
    }
    return `${minutes} mins`;
};

const allClientGroups = [
    { name: "Proval Net", clients: ["proval net", "proval"] },
    { name: "MMA", clients: ["anm", "mma"] },
    { name: "A1 AMC", clients: ["a1 amc", "a1"] },
    { name: "AAG", clients: ["aag"] },
    { name: "AAMS", clients: ["aams"] },
    { name: "All Pro Amc", clients: ["all pro amc", "all pro"] },
    { name: "Appraisal Partner", clients: ["appraisal partner"] },
    { name: "Appraisers Review", clients: ["appraisers review"] },
    { name: "Ascribe VL", clients: ["ascribe vl", "ascribe"] },
    { name: "Core Valuation", clients: ["core valuation", "core"] },
    { name: "EA - Appraisals", clients: ["ea - appraisals", "ea"] },
    { name: "Eppraisal", clients: ["eppraisal"] },
    { name: "Exactus", clients: ["exactus"] },
    { name: "Financial Dimensions", clients: ["financial dimensions"] },
    { name: "Inspectify - Hybrid", clients: ["inspectify - hybrid", "inspectify"] },
    { name: "LRES - Appraisals", clients: ["lres - appraisals", "lres"] },
    { name: "LVS", clients: ["lvs"] },
    { name: "NAN", clients: ["nan"] },
    { name: "Property Valuation Network", clients: ["property valuation network", "pvn"] },
    { name: "Signature", clients: ["signature"] },
    { name: "Team Matthew Appraisals", clients: ["team matthew appraisals", "team matthew"] },
    { name: "Top Notch appraisers", clients: ["top notch appraisers", "top notch"] },
    { name: "Valora AMC", clients: ["valora amc", "valora"] },
    { name: "Valutrust Solutions", clients: ["valutrust solutions", "valutrust"] },
    { name: "Zap Appraisal", clients: ["zap appraisal", "zap"] },
    { name: "Zap New", clients: ["zap new"] }
];

const StatsOverview = ({
    aggregatedSummary,
    activeClient,
    shiftFilter,
    viewMode,
    files = [],
    startDate: dashboardStart,
    endDate: dashboardEnd,
    notifications = [],
    onMarkAllRead,
    onMarkSingleRead,
    location
}) => {
    const todayStr = new Date().toISOString().split("T")[0];
    const token = localStorage.getItem("token");
    const itemsPerPage = 10;
    const empItemsPerPage = 20;

    const [inflowStart, setInflowStart] = useState(todayStr);
    const [inflowEnd, setInflowEnd] = useState(todayStr);
    const [inflowShift, setInflowShift] = useState("Live");
    const [inflowData, setInflowData] = useState([]);

    const [outflowStart, setOutflowStart] = useState(todayStr);
    const [outflowEnd, setOutflowEnd] = useState(todayStr);
    const [outflowShift, setOutflowShift] = useState("Live");
    const [outflowData, setOutflowData] = useState([]);
    const [graphFilter, setGraphFilter] = useState("All");

    const [metrics, setMetrics] = useState({
        total: 0,
        assigned: 0,
        unassigned: 0,
        inProgress: 0,
        completed: 0,
        rejected: 0,
        exceptions: 0
    });

    const [portalBreakdown, setPortalBreakdown] = useState([]);
    const [employeeData, setEmployeeData] = useState([]);
    const [clientBreakdownData, setClientBreakdownData] = useState([]);
    const [empPage, setEmpPage] = useState(1);
    const [portalPage, setPortalPage] = useState(1);
    const [expandedReviewers, setExpandedReviewers] = useState({});
    const [expandedActiveFiles, setExpandedActiveFiles] = useState({});

    const toggleReviewerExpand = (empName) => {
        setExpandedReviewers(prev => ({
            ...prev,
            [empName]: !prev[empName]
        }));
    };

    const toggleActiveFilesExpand = (empName) => {
        setExpandedActiveFiles(prev => ({
            ...prev,
            [empName]: !prev[empName]
        }));
    };

    const maxPortalVolume = portalBreakdown.length > 0
        ? Math.max(...portalBreakdown.map(p => Number(p.total || 0)), 1)
        : 1;

    const scrollContainerStyle = {
        maxHeight: "460px",
        overflowY: "auto",
        border: "1px solid #e2e8f0",
        borderRadius: "8px"
    };

    const tableHeaderStyle = {
        padding: "12px 16px",
        //textTransform: "uppercase",
        fontSize: "12px",
        fontWeight: "750",
        backgroundColor: "#f8fafc",
        borderBottom: "2px solid #e2e8f0",
        position: "sticky",
        top: 0,
        zIndex: 10
    };

    const cellStyle = {
        padding: "10px 16px",
        borderBottom: "1px solid #edf2f7",
        textAlign: "center"
    };

    const footerStyle = {
        padding: "12px 16px",
        backgroundColor: "#f1f5f9",
        fontWeight: "900",
        fontSize: "13px",
        borderTop: "2px solid #cbd5e1",
        position: "sticky",
        bottom: 0,
        zIndex: 5
    };

    const paginatorButtonStyle = (disabled) => ({
        padding: "4px 10px",
        fontSize: "11px",
        fontWeight: "800",
        border: "1px solid #cbd5e1",
        borderRadius: "4px",
        backgroundColor: disabled ? "#f1f5f9" : "white",
        color: disabled ? "#94a3b8" : "#334155",
        cursor: disabled ? "not-allowed" : "pointer"
    });

    const shiftToggleStyle = (isActive, activeColor) => ({
        padding: "6px 12px",
        fontSize: "12px",
        fontWeight: "800",
        border: "1px solid #cbd5e1",
        borderRadius: "6px",
        cursor: "pointer",
        backgroundColor: isActive ? activeColor : "white",
        color: isActive ? "white" : "#475569"
    });

    const formatHourLabel = (h) =>
        `${String(h).padStart(2, "0")}:00 - ${String((h + 1) % 24).padStart(2, "0")}:00`;

    const getShiftSequence = (shiftType) => {
        if (shiftType === "Live") return Array.from({ length: 12 }).map((_, i) => i + 5);
        if (shiftType === "Offline") return Array.from({ length: 12 }).map((_, i) => (i + 17) % 24);
        return Array.from({ length: 24 }).map((_, i) => i);
    };

    useEffect(() => {
        if (viewMode !== "Hourly") return;

        const fetchInflow = async () => {
            try {
                const res = await axios.get("/api/dashboard/inflow-timeline", {
                    params: {
                        startDate: inflowStart,
                        endDate: inflowEnd,
                        client: activeClient,
                        shiftFilter: inflowShift,
                        location: location
                    },
                    headers: { Authorization: `Bearer ${token}` }
                });

                const sequence = getShiftSequence(inflowShift).map(hour => {
                    const match = (res.data || []).find(r => Number(r.hr) === hour);
                    return match ? { hour, qcIn: match.qcIn, revIn: match.revIn } : { hour, qcIn: 0, revIn: 0 };
                });

                setInflowData(sequence);
            } catch (err) {
                console.error("Inflow timeline fetch fault:", err);
            }
        };

        fetchInflow();
    }, [inflowStart, inflowEnd, activeClient, inflowShift, viewMode, token, location]);

    useEffect(() => {
        if (viewMode !== "Hourly") return;

        const fetchOutflow = async () => {
            try {
                const res = await axios.get("/api/dashboard/outflow-timeline", {
                    params: {
                        startDate: outflowStart,
                        endDate: outflowEnd,
                        client: activeClient,
                        shiftFilter: outflowShift,
                        location: location
                    },
                    headers: { Authorization: `Bearer ${token}` }
                });

                const sequence = getShiftSequence(outflowShift).map(hour => {
                    const match = (res.data || []).find(r => Number(r.hr) === hour);
                    return match ? { hour, qcOut: match.qcOut, revOut: match.revOut } : { hour, qcOut: 0, revOut: 0 };
                });

                setOutflowData(sequence);
            } catch (err) {
                console.error("Outflow timeline fetch fault:", err);
            }
        };

        fetchOutflow();
    }, [outflowStart, outflowEnd, activeClient, outflowShift, viewMode, token, location]);

    useEffect(() => {
        if (viewMode !== "Summary" && viewMode !== "Report") return;

        if (aggregatedSummary && aggregatedSummary.isBackendAggregated && aggregatedSummary.metrics) {
            const { metrics: serverMetrics, portals = [], reviewers = [] } = aggregatedSummary;

            setMetrics({
                total: serverMetrics.total ?? 0,
                assigned: serverMetrics.assigned ?? 0,
                unassigned: serverMetrics.unassigned ?? 0,
                inProgress: serverMetrics.inProgress ?? 0,
                completed: serverMetrics.completed ?? 0,
                rejected: serverMetrics.rejected ?? 0,
                exceptions: serverMetrics.exceptions ?? 0
            });

            const normalizedPortals = (portals || []).map(p => ({
                path: `${p.client_clean} > ${p.dept_clean}`,
                vip: p.vip ?? 0,
                rush: p.rush ?? 0,
                due: p.due ?? 0,
                past: p.past ?? 0,
                late: p.late ?? 0,
                total: p.total ?? 0,
                unassigned: p.unassigned ?? 0,
                assigned: p.assigned ?? 0,
                inProgress: p.inProgress ?? 0,
                completed: p.completed ?? 0,
                rejected: p.rejected ?? 0,
                exceptions: p.exceptions ?? 0,
                within: p.within ?? 0,
                over: p.over ?? 0,
                client_clean: p.client_clean,
                dept_clean: p.dept_clean
            })).sort((a, b) => Number(b.total || 0) - Number(a.total || 0));

            setPortalBreakdown(normalizedPortals);

            setEmployeeData(
                (reviewers || [])
                    .map(r => ({
                        name: r.name,
                        assigned: Number(r.assigned ?? 0),
                        working: Number(r.working ?? 0),
                        completed: Number(r.completed ?? 0),
                        rejected: Number(r.rejected ?? 0),
                        exceptions: Number(r.exceptions ?? 0),
                        oldest_assign_time: r.oldest_assign_time || null,
                        active_files: r.active_files || [],
                        portals: r.portals || {}
                    }))
                    .sort((a, b) => {
                        const aIsUnassignedRow = String(a.name || "").toLowerCase().includes("unassigned");
                        const bIsUnassignedRow = String(b.name || "").toLowerCase().includes("unassigned");
                        if (aIsUnassignedRow !== bIsUnassignedRow) {
                            return aIsUnassignedRow ? 1 : -1;
                        }
                        if (b.assigned !== a.assigned) {
                            return b.assigned - a.assigned;
                        }
                        if (b.working !== a.working) {
                            return b.working - a.working;
                        }
                        const totalDoneA = a.completed + a.rejected + a.exceptions;
                        const totalDoneB = b.completed + b.rejected + b.exceptions;
                        if (totalDoneB !== totalDoneA) {
                            return totalDoneB - totalDoneA;
                        }
                        return String(a.name || "").localeCompare(String(b.name || ""));
                    })
            );

            // 1. Filter the master group configuration array down to only the active selection unless "All" is chosen
            const filteredGroups = activeClient && activeClient !== "All" && activeClient !== "All Clients" && activeClient !== "Dashboard"
                ? allClientGroups.filter(group => group.name.toLowerCase() === activeClient.toLowerCase())
                : allClientGroups;

            // 2. Map through the filtered client groups to perform accurate status field aggregation
            const computedBreakdown = filteredGroups.map(group => {
                const matchedPortals = normalizedPortals.filter(p => {
                    const item = String(p.client_clean || "").toLowerCase().trim();
                    return group.clients.some(c => {
                        const cleanC = c.toLowerCase().trim();
                        return item === cleanC || 
                               item.split(/\s+/).includes(cleanC) || 
                               cleanC.split(/\s+/).includes(item);
                    });
                });

                const qcPortals = matchedPortals.filter(p => String(p.dept_clean || "").toLowerCase() === "qc");
                const revPortals = matchedPortals.filter(p =>
                    String(p.dept_clean || "").toLowerCase() === "revised" ||
                    String(p.dept_clean || "").toLowerCase() === "report writing"
                );

                const aggregateFields = (list) => ({
                    total: list.reduce((acc, p) => acc + Number(p.totalFiles || p.total || 0), 0),
                    unassign: list.reduce((acc, p) => acc + Number(p.unassigned || 0), 0),
                    working: list.reduce((acc, p) => acc + Number(p.inProgress || 0), 0),
                    completed: list.reduce((acc, p) => acc + Number(p.completed || 0), 0),
                    rejected: list.reduce((acc, p) => acc + Number(p.rejected || 0), 0),
                    exceptions: list.reduce((acc, p) => acc + Number(p.exceptions || 0), 0)
                });

                return {
                    name: group.name,
                    qc: aggregateFields(qcPortals),
                    rev: aggregateFields(revPortals)
                };
            });

            const isSingleClientSelected = activeClient && activeClient !== "All" && activeClient !== "All Clients" && activeClient !== "Dashboard";
            const filteredBreakdown = isSingleClientSelected
                ? computedBreakdown
                : computedBreakdown.filter(item => (Number(item.qc.total) + Number(item.rev.total)) > 0);

            setClientBreakdownData(filteredBreakdown);
        }
    }, [aggregatedSummary, viewMode]);

    const totalQCIn = inflowData.reduce((acc, curr) => acc + Number(curr.qcIn || 0), 0);
    const totalRevIn = inflowData.reduce((acc, curr) => acc + Number(curr.revIn || 0), 0);
    const combinedInflowTotal = totalQCIn + totalRevIn;

    const totalQCOut = outflowData.reduce((acc, curr) => acc + Number(curr.qcOut || 0), 0);
    const totalRevOut = outflowData.reduce((acc, curr) => acc + Number(curr.revOut || 0), 0);
    const combinedOutflowTotal = totalQCOut + totalRevOut;

    const totalEmpPages = Math.ceil((employeeData || []).length / empItemsPerPage);
    const paginatedEmployees = (employeeData || []).slice((empPage - 1) * empItemsPerPage, empPage * empItemsPerPage);

    const totalPortalPages = Math.ceil((portalBreakdown || []).length / itemsPerPage);
    const paginatedPortals = (portalBreakdown || []).slice((portalPage - 1) * itemsPerPage, portalPage * itemsPerPage);

    const BarRow = ({ label, count, max, color }) => {
        const safeCount = Number(count || 0);
        const width = max > 0 ? Math.max((safeCount / max) * 100, safeCount > 0 ? 3 : 0) : 0;

        return (
            <div style={{ display: "grid", gridTemplateColumns: "150px 1fr 55px", gap: "12px", alignItems: "center" }}>
                <div style={{ fontSize: "12px", fontWeight: "800", color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {label}
                </div>
                <div style={{ height: "16px", backgroundColor: "#eef2f7", borderRadius: "4px", overflow: "hidden" }}>
                    <div style={{ width: `${width}%`, height: "100%", backgroundColor: color, borderRadius: "4px" }} />
                </div>
                <div style={{ fontSize: "13px", fontWeight: "900", color: "#0f172a", textAlign: "right" }}>
                    {safeCount}
                </div>
            </div>
        );
    };

    const DonutChart = ({ percent, label, color }) => (
        <div style={{ display: "flex", alignItems: "center", gap: "22px" }}>
            <div style={{
                width: "132px",
                height: "132px",
                borderRadius: "50%",
                background: `conic-gradient(${color} ${percent * 3.6}deg, #e2e8f0 0deg)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}>
                <div style={{
                    width: "86px",
                    height: "86px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column"
                }}>
                    <div style={{ fontSize: "24px", fontWeight: "900", color }}>{percent}%</div>
                    <div style={{ fontSize: "10px", fontWeight: "800", color: "#64748b" }}>{label}</div>
                </div>
            </div>
        </div>
    );

    // --- WORKSPACE 2 VIEW: HOURLY TIMELINES Flow Monitor ---
    if (viewMode === "Hourly") {
        return (
            <div style={{ padding: "30px", overflowY: "auto" }}>
                <div style={{ marginBottom: "30px", backgroundColor: "white", borderRadius: "12px", padding: "24px", border: "1px solid #edf2f7" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
                        <h3 style={{ fontSize: "16px", color: "#0369a1", margin: 0, fontWeight: "900" }}>Incoming Inflow Timeline Monitor</h3>
                        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                            <div style={{ display: "flex", gap: "4px", backgroundColor: "#f1f5f9", padding: "4px", borderRadius: "8px" }}>
                                <button onClick={() => setInflowShift("Live")} style={shiftToggleStyle(inflowShift === "Live", "#0284c7")}>Live</button>
                                <button onClick={() => setInflowShift("Offline")} style={shiftToggleStyle(inflowShift === "Offline", "#0369a1")}>Offline</button>
                                <button onClick={() => setInflowShift("All")} style={shiftToggleStyle(inflowShift === "All", "#1e293b")}>Total</button>
                            </div>
                            <input type="date" value={inflowStart} onChange={(e) => setInflowStart(e.target.value)} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }} />
                            <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "800" }}>to</span>
                            <input type="date" value={inflowEnd} onChange={(e) => setInflowEnd(e.target.value)} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }} />
                        </div>
                    </div>

                    <div style={{ ...scrollContainerStyle, maxHeight: "350px" }} className="custom-scrollbar">
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead>
                                <tr>
                                    <th style={{ ...tableHeaderStyle, textAlign: "left" }}>Hour Window</th>
                                    <th style={{ ...tableHeaderStyle, color: "#0ea5e9" }}>QC Inflow</th>
                                    <th style={{ ...tableHeaderStyle, color: "#0284c7" }}>Revised Inflow</th>
                                    <th style={{ ...tableHeaderStyle, color: "#1e293b" }}>Combined Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inflowData.map((row, idx) => {
                                    const slotTotal = Number(row.qcIn || 0) + Number(row.revIn || 0);
                                    return (
                                        <tr key={idx} style={{ backgroundColor: slotTotal > 0 ? "#f0f9ff" : "transparent" }}>
                                            <td style={{ ...cellStyle, textAlign: "left", fontWeight: "700" }}>{formatHourLabel(row.hour)}</td>
                                            <td style={{ ...cellStyle, fontWeight: "900", color: row.qcIn > 0 ? "#0ea5e9" : "#cbd5e1" }}>{row.qcIn}</td>
                                            <td style={{ ...cellStyle, fontWeight: "900", color: row.revIn > 0 ? "#0284c7" : "#cbd5e1" }}>{row.revIn}</td>
                                            <td style={{ ...cellStyle, fontWeight: "900", color: slotTotal > 0 ? "#1e293b" : "#cbd5e1" }}>{slotTotal}</td>
                                        </tr>
                                    );
                                })}
                                <tr>
                                    <td style={{ ...footerStyle, textAlign: "left" }}>TOTAL SUMMARY COUNT</td>
                                    <td style={{ ...footerStyle, color: "#0ea5e9" }}>{totalQCIn}</td>
                                    <td style={{ ...footerStyle, color: "#0284c7" }}>{totalRevIn}</td>
                                    <td style={{ ...footerStyle, backgroundColor: "#0369a1", color: "white" }}>{combinedInflowTotal}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={{ marginBottom: "30px", backgroundColor: "white", borderRadius: "12px", padding: "24px", border: "1px solid #edf2f7" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
                        <h3 style={{ fontSize: "16px", color: "#6d28d9", margin: 0, fontWeight: "900" }}>Finished Outflow Production Monitor</h3>
                        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                            <div style={{ display: "flex", gap: "4px", backgroundColor: "#f1f5f9", padding: "4px", borderRadius: "8px" }}>
                                <button onClick={() => setOutflowShift("Live")} style={shiftToggleStyle(outflowShift === "Live", "#8b5cf6")}>Live</button>
                                <button onClick={() => setOutflowShift("Offline")} style={shiftToggleStyle(outflowShift === "Offline", "#6d28d9")}>Offline</button>
                                <button onClick={() => setOutflowShift("All")} style={shiftToggleStyle(outflowShift === "All", "#1e293b")}>Total</button>
                            </div>
                            <input type="date" value={outflowStart} onChange={(e) => setOutflowStart(e.target.value)} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }} />
                            <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "800" }}>to</span>
                            <input type="date" value={outflowEnd} onChange={(e) => setOutflowEnd(e.target.value)} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }} />
                        </div>
                    </div>

                    <div style={{ ...scrollContainerStyle, maxHeight: "350px" }} className="custom-scrollbar">
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead>
                                <tr>
                                    <th style={{ ...tableHeaderStyle, textAlign: "left" }}>Hour Window</th>
                                    <th style={{ ...tableHeaderStyle, color: "#8b5cf6" }}>QC Outflow</th>
                                    <th style={{ ...tableHeaderStyle, color: "#7c3aed" }}>Revised Outflow</th>
                                    <th style={{ ...tableHeaderStyle, color: "#1e293b" }}>Combined Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {outflowData.map((row, idx) => {
                                    const slotTotal = Number(row.qcOut || 0) + Number(row.revOut || 0);
                                    return (
                                        <tr key={idx} style={{ backgroundColor: slotTotal > 0 ? "#faf5ff" : "transparent" }}>
                                            <td style={{ ...cellStyle, textAlign: "left", fontWeight: "700" }}>{formatHourLabel(row.hour)}</td>
                                            <td style={{ ...cellStyle, fontWeight: "900", color: row.qcOut > 0 ? "#8b5cf6" : "#cbd5e1" }}>{row.qcOut}</td>
                                            <td style={{ ...cellStyle, fontWeight: "900", color: row.revOut > 0 ? "#7c3aed" : "#cbd5e1" }}>{row.revOut}</td>
                                            <td style={{ ...cellStyle, fontWeight: "900", color: slotTotal > 0 ? "#1e293b" : "#cbd5e1" }}>{slotTotal}</td>
                                        </tr>
                                    );
                                })}
                                <tr>
                                    <td style={{ ...footerStyle, textAlign: "left" }}>TOTAL SUMMARY COUNT</td>
                                    <td style={{ ...footerStyle, color: "#8b5cf6" }}>{totalQCOut}</td>
                                    <td style={{ ...footerStyle, color: "#7c3aed" }}>{totalRevOut}</td>
                                    <td style={{ ...footerStyle, backgroundColor: "#6d28d9", color: "white" }}>{combinedOutflowTotal}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <style>
                    {`
                        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
                        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }
                        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #a0aec0; }
                    `}
                </style>
            </div>
        );
    }

    // --- 📊 WORKSPACE 3 VIEW: SEPARATE ANALYTICS & EXECUTIVE GRAPHICS PERFORMANCE REPORT ---
    if (viewMode === "Report") {
        const totalVip = portalBreakdown.reduce((acc, curr) => acc + Number(curr.vip || 0), 0);
        const totalRush = portalBreakdown.reduce((acc, curr) => acc + Number(curr.rush || 0), 0);
        const totalDue = portalBreakdown.reduce((acc, curr) => acc + Number(curr.due || 0), 0);
        const totalPast = portalBreakdown.reduce((acc, curr) => acc + Number(curr.past || 0), 0);
        const totalLate = portalBreakdown.reduce((acc, curr) => acc + Number(curr.late || 0), 0);
        const combinedPriorityFiles = totalVip + totalRush + totalDue + totalPast + totalLate;

        const totalWithinSla = portalBreakdown.reduce((acc, curr) => acc + Number(curr.within || 0), 0);
        const totalOverSla = portalBreakdown.reduce((acc, curr) => acc + Number(curr.over || 0), 0);
        const combinedSlaCalculated = totalWithinSla + totalOverSla;
        const globalSlaAccuracyPct = combinedSlaCalculated > 0 ? Math.round((totalWithinSla / combinedSlaCalculated) * 100) : 100;

        const completedCount = Number(metrics.completed || 0);
        const inProgressCount = Number(metrics.inProgress || 0);
        const rejectedCount = Number(metrics.rejected || 0);
        const workloadDenominator = completedCount + inProgressCount + rejectedCount || 1;

        const completedPercentage = Math.round((completedCount / workloadDenominator) * 100);
        const inProgressPercentage = Math.round((inProgressCount / workloadDenominator) * 100);
        const rejectedPercentage = (completedCount || inProgressCount || rejectedCount)
            ? (100 - completedPercentage - inProgressPercentage)
            : 0;

        const priorityRows = [
            { label: "VIP Requests", count: totalVip, color: "#ef4444" },
            { label: "Rush Milestones", count: totalRush, color: "#f97316" },
            { label: "Due Date Boundary", count: totalDue, color: "#3b82f6" },
            { label: "Past Due Escalations", count: totalPast, color: "#8b5cf6" },
            { label: "Late Records Log", count: totalLate, color: "#be123c" }
        ];

        // Filter: Show ONLY clients that actually have active file metrics logged
        const activeClientsForGraph = clientBreakdownData.filter(
            c => (Number(c.qc?.total || 0) + Number(c.rev?.total || 0)) > 0
        );

        const maxCombinedVolume = Math.max(
            ...activeClientsForGraph.map(c => Number(c.qc?.total || 0) + Number(c.rev?.total || 0)),
            1
        );

        const maxQCVolume = Math.max(
            ...activeClientsForGraph.map(c => Number(c.qc?.total || 0)),
            1
        );

        const maxRevisedVolume = Math.max(
            ...activeClientsForGraph.map(c => Number(c.rev?.total || 0)),
            1
        );

        return (
            <div style={{ padding: "30px", backgroundColor: "#f8fafc", overflowY: "auto" }} className="pdf-page-container">

                {/* 📄 BRAND REPORT BANNER */}
                <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "24px", marginBottom: "24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15,23,42,0.04)" }} className="pdf-card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "18px", flexWrap: "wrap" }}>
                        <div>
                            <h2 style={{ fontSize: "22px", color: "#0f172a", margin: "0 0 6px 0", fontWeight: "900" }}>
                                Executive Production Analytics Report
                            </h2>
                            <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>
                                Operational data analysis window: <b>{dashboardStart}</b> to <b>{dashboardEnd}</b>
                            </p>
                        </div>
                        <div style={{ padding: "8px 16px", backgroundColor: "#f5f3ff", color: "#6d28d9", borderRadius: "999px", fontSize: "13px", fontWeight: "900", border: "1px solid #ddd6fe" }}>
                            🏆 Global SLA Integrity: {globalSlaAccuracyPct}% Accuracy
                        </div>
                    </div>
                </div>

                {/* 📈 COMPONENT SECTION 1: DATA DIALS AND TIMELINE PROGRESS GRAPH */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "24px" }} className="pdf-grid-row">
                    <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "24px", border: "1px solid #e2e8f0" }} className="pdf-card">
                        <h4 style={{ margin: "0 0 20px 0", fontSize: "15px", color: "#0f172a", fontWeight: "900" }}>
                            📊 Production Workload Status Ratios
                        </h4>
                        <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center", gap: "14px", flexWrap: "wrap", padding: "10px 0" }}>
                            <DonutChart percent={completedPercentage} label="Completed" color="#10b981" />
                            <DonutChart percent={inProgressPercentage} label="In Progress" color="#f59e0b" />
                            <DonutChart percent={rejectedPercentage} label="Rejected" color="#ef4444" />
                        </div>
                    </div>

                    <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "24px", border: "1px solid #e2e8f0" }} className="pdf-card">
                        <h4 style={{ margin: "0 0 16px 0", fontSize: "15px", color: "#0f172a", fontWeight: "900" }}>
                            ⏱ Turnaround Speed Metrics
                        </h4>
                        <div style={{ display: "flex", flexDirection: "column", gap: "18px", marginTop: "10px" }}>
                            <div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontWeight: "800", color: "#475569", marginBottom: "6px" }}>
                                    <span>Within SLA Buffer Frame</span>
                                    <span style={{ color: "#16a34a" }}>{totalWithinSla} Files ({globalSlaAccuracyPct}%)</span>
                                </div>
                                <div style={{ height: "18px", backgroundColor: "#f1f5f9", borderRadius: "6px", overflow: "hidden" }}>
                                    <div style={{ width: `${globalSlaAccuracyPct}%`, backgroundColor: "#16a34a", height: "100%", borderRadius: "6px" }} />
                                </div>
                            </div>
                            <div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontWeight: "800", color: "#475569", marginBottom: "6px" }}>
                                    <span>Over SLA Threshold Breaches</span>
                                    <span style={{ color: "#dc2626" }}>{totalOverSla} Files ({100 - globalSlaAccuracyPct}%)</span>
                                </div>
                                <div style={{ height: "18px", backgroundColor: "#f1f5f9", borderRadius: "6px", overflow: "hidden" }}>
                                    <div style={{ width: `${100 - globalSlaAccuracyPct}%`, backgroundColor: "#dc2626", height: "100%", borderRadius: "6px" }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 📊 COMPONENT SECTION 2: VOLUME SPREAD PROGRESS GRAPH */}
                <div className="page-break-before">
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "24px" }} className="pdf-grid-row">
                        <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "24px", border: "1px solid #e2e8f0" }} className="pdf-card">
                            <h4 style={{ margin: "0 0 20px 0", fontSize: "15px", color: "#0f172a", fontWeight: "900" }}>
                                🔥 Volumetric Mix by Priority Tiers
                            </h4>
                            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                {priorityRows.map(row => (
                                    <BarRow key={row.label} label={row.label} count={row.count} max={Math.max(combinedPriorityFiles, 1)} color={row.color} />
                                ))}
                            </div>
                        </div>
                        <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "24px", border: "1px solid #e2e8f0" }} className="pdf-card">
                            <h4 style={{ margin: "0 0 15px 0", fontSize: "15px", color: "#0f172a", fontWeight: "900" }}>
                                🚀 Target Portal Load Rankings
                            </h4>
                            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                                {portalBreakdown.slice(0, 5).map((portal, idx) => (
                                    <BarRow key={`${portal.path}-${idx}`} label={portal.path} count={portal.total} max={maxPortalVolume} color="#2563eb" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ✨ Client Volumes (QC vs Revised Horizontal Breakdown) */}
                <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "24px", border: "1px solid #e2e8f0", display: "flex", flexDirection: "column", marginBottom: "24px" }} className="pdf-card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px", flexWrap: "wrap", gap: "12px" }}>
                        <h4 style={{ margin: 0, fontSize: "15px", color: "#0f172a", fontWeight: "900" }}>
                            📊 Client Volumes (QC vs Revised Breakdown)
                        </h4>
                        <div style={{ display: "flex", gap: "4px", backgroundColor: "#f1f5f9", padding: "4px", borderRadius: "8px" }} className="top-filter-bar-print-hide">
                            <button onClick={() => setGraphFilter("All")} style={shiftToggleStyle(graphFilter === "All", "#1e293b")}>All</button>
                            <button onClick={() => setGraphFilter("QC")} style={shiftToggleStyle(graphFilter === "QC", "#0ea5e9")}>QC</button>
                            <button onClick={() => setGraphFilter("Revised")} style={shiftToggleStyle(graphFilter === "Revised", "#7c3aed")}>Revised</button>
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: "16px", fontSize: "11px", color: "#475569", fontWeight: "bold", marginBottom: "20px" }}>
                        {(graphFilter === "All" || graphFilter === "QC") && <div style={{ display: "flex", alignItems: "center", gap: "4px" }}><span style={{ width: "12px", height: "12px", backgroundColor: "#0ea5e9", borderRadius: "3px" }} /> QC Ingest</div>}
                        {(graphFilter === "All" || graphFilter === "Revised") && <div style={{ display: "flex", alignItems: "center", gap: "4px" }}><span style={{ width: "12px", height: "12px", backgroundColor: "#7c3aed", borderRadius: "3px" }} /> Revised Work</div>}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                        {activeClientsForGraph.map((client) => {
                            const qcVal = Number(client.qc?.total || 0);
                            const revVal = Number(client.rev?.total || 0);

                            let qcWidth = 0;
                            let revWidth = 0;
                            let labelText = "";

                            if (graphFilter === "All") {
                                qcWidth = (qcVal / maxCombinedVolume) * 100;
                                revWidth = (revVal / maxCombinedVolume) * 100;
                                labelText = `${qcVal} QC / ${revVal} Rev`;
                            } else if (graphFilter === "QC") {
                                qcWidth = (qcVal / maxQCVolume) * 100;
                                revWidth = 0;
                                labelText = `${qcVal} QC`;
                            } else if (graphFilter === "Revised") {
                                qcWidth = 0;
                                revWidth = (revVal / maxRevisedVolume) * 100;
                                labelText = `${revVal} Rev`;
                            }

                            return (
                                <div key={client.name} style={{ display: "grid", gridTemplateColumns: "180px 1fr 120px", gap: "16px", alignItems: "center" }}>
                                    <div style={{ fontSize: "12px", fontWeight: "800", color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={client.name}>
                                        {client.name}
                                    </div>
                                    <div style={{ height: "18px", backgroundColor: "#f1f5f9", borderRadius: "4px", overflow: "hidden", display: "flex", width: "100%" }}>
                                        {qcWidth > 0 && (
                                            <div style={{ width: `${qcWidth}%`, backgroundColor: "#0ea5e9", height: "100%", transition: "width 0.3s ease" }} title={`${client.name} QC: ${qcVal}`} />
                                        )}
                                        {revWidth > 0 && (
                                            <div style={{ width: `${revWidth}%`, backgroundColor: "#7c3aed", height: "100%", transition: "width 0.3s ease" }} title={`${client.name} Revised: ${revVal}`} />
                                        )}
                                    </div>
                                    <div style={{ fontSize: "11px", fontWeight: "800", color: "#475569", textAlign: "right" }}>
                                        {labelText}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* 📑 BOTTOM AUDIT MATRIX SHEET */}
                <div className="page-break-before">
                    <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "24px", border: "1px solid #e2e8f0" }} className="pdf-card">
                        <h4 style={{ margin: "0 0 16px 0", fontSize: "15px", color: "#0f172a", fontWeight: "900" }}>
                            📋 Performance Audit Data Breakdown
                        </h4>
                        <div style={{ ...scrollContainerStyle, maxHeight: "400px" }} className="pdf-scroll-hide">
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                                        <th style={{ ...tableHeaderStyle, textAlign: "left" }}>Portal Workspace</th>
                                        <th style={{ ...tableHeaderStyle, textAlign: "center" }}>Volume</th>
                                        <th style={{ ...tableHeaderStyle, textAlign: "center", color: "#16a34a" }}>Within SLA</th>
                                        <th style={{ ...tableHeaderStyle, textAlign: "center", color: "#dc2626" }}>Over SLA</th>
                                        <th style={{ ...tableHeaderStyle, textAlign: "center" }}>SLA Hit Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {portalBreakdown.map((portal, idx) => {
                                        const totalPortalSla = Number(portal.within || 0) + Number(portal.over || 0);
                                        const portalSlaHitRatePct = totalPortalSla > 0 ? Math.round((Number(portal.within || 0) / totalPortalSla) * 100) : 100;
                                        return (
                                            <tr key={`${portal.path}-${idx}`} style={{ borderBottom: "1px solid #edf2f7" }}>
                                                <td style={{ ...cellStyle, textAlign: "left", fontWeight: "700", color: "#334155" }}>{portal.path}</td>
                                                <td style={{ ...cellStyle, fontWeight: "900", color: "#0f172a" }}>{portal.total}</td>
                                                <td style={{ ...cellStyle, color: "#16a34a", fontWeight: "900" }}>{portal.within}</td>
                                                <td style={{ ...cellStyle, color: "#dc2626", fontWeight: "900" }}>{portal.over}</td>
                                                <td style={{ ...cellStyle, verticalAlign: "middle" }}>
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                                        <div style={{ width: "120px", height: "8px", backgroundColor: "#fee2e2", borderRadius: "4px", overflow: "hidden", display: "flex" }}>
                                                            <div style={{ width: `${portalSlaHitRatePct}%`, backgroundColor: "#22c55e", height: "100%" }} />
                                                        </div>
                                                        <span style={{ fontWeight: "900", color: portalSlaHitRatePct >= 85 ? "#16a34a" : "#d97706", minWidth: "35px", textAlign: "right" }}>
                                                            {portalSlaHitRatePct}%
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* ✨ CSS PRINT MEDIA ENGINE ENHANCEMENTS */}
                <style>
                    {`
                        @media print {
                            * {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            body, html, #root {
                                background: white !important;
                                color: black !important;
                                height: auto !important;
                                min-height: auto !important;
                                overflow: visible !important;
                                width: 100% !important;
                            }
                            aside, .top-filter-bar-print-hide, button, select, input, label {
                                display: none !important;
                            }
                            .main-portal-container {
                                height: auto !important;
                                min-height: auto !important;
                                max-height: none !important;
                                display: block !important;
                                overflow: visible !important;
                            }
                            .dashboard-print-root, .pdf-page-container {
                                padding: 0 !important;
                                margin: 0 !important;
                                width: 100% !important;
                                max-width: 100% !important;
                                display: block !important;
                                height: auto !important;
                                min-height: auto !important;
                                max-height: none !important;
                                overflow: visible !important;
                            }
                            th {
                                position: static !important;
                            }
                            .pdf-card {
                                border: 1px solid #e2e8f0 !important;
                                border-radius: 12px !important;
                                padding: 20px !important;
                                margin-bottom: 20px !important;
                                page-break-inside: avoid !important;
                                background: white !important;
                            }
                            .pdf-grid-row {
                                display: flex !important;
                                flex-direction: row !important;
                                gap: 20px !important;
                                width: 100% !important;
                            }
                            .pdf-grid-row > div {
                                flex: 1 !important;
                                width: 50% !important;
                            }
                            .pdf-scroll-hide {
                                max-height: none !important;
                                overflow: visible !important;
                            }
                            .page-break-before {
                                page-break-before: always !important;
                                break-before: page !important;
                            }
                        }
                        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
                        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }
                        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #a0aec0; }
                    `}
                </style>
            </div>
        );
    }

    // --- WORKSPACE 1 VIEW: SUMMARY METRICS & EMPLOYEE USER GRIDS ---
    return (
        <div style={{ padding: "30px", overflowY: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "10px", marginBottom: "30px" }}>
                {[
                    { label: "Total Files", count: metrics.total, color: "#3182ce", bg: "#ebf8ff" },
                    { label: "Unassigned", count: metrics.unassigned, color: "#38a169", bg: "#f0fff4" },
                    { label: "Assigned", count: metrics.assigned, color: "#00a3e0", bg: "#e6f7ff" },
                    { label: "In Progress", count: metrics.inProgress, color: "#d69e2e", bg: "#fffff0" },
                    { label: "Completed", count: metrics.completed, color: "#2f855a", bg: "#f0fff4" },
                    { label: "Rejected", count: metrics.rejected, color: "#e53e3e", bg: "#fff5f5" },
                    { label: "Exceptions", count: metrics.exceptions || 0, color: "#7c3aed", bg: "#f5f3ff" }
                ].map((card, idx) => (
                    <div key={idx} style={{ backgroundColor: card.bg, borderRadius: "10px", padding: "15px", border: `1px solid ${card.color}20`, textAlign: "center" }}>
                        <div style={{ fontSize: "20px", fontWeight: "900", color: card.color }}>{card.count}</div>
                        <div style={{ fontSize: "10px", color: "#666", marginTop: "5px" }}>{card.label}</div>
                    </div>
                ))}
            </div>

            <div style={{ marginBottom: "24px", backgroundColor: "white", borderRadius: "10px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                    <h3 style={{ fontSize: "16px", color: "#333", fontWeight: "900", margin: 0 }}>⚡ Portal-wise Priority Breakdown</h3>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <button onClick={() => setPortalPage(p => Math.max(1, p - 1))} disabled={portalPage === 1} style={paginatorButtonStyle(portalPage === 1)}>Prev</button>
                        <span style={{ fontSize: "12px", fontWeight: "900" }}>Page {portalPage} of {Math.max(1, totalPortalPages)}</span>
                        <button onClick={() => setPortalPage(p => Math.min(totalPortalPages, p + 1))} disabled={portalPage >= totalPortalPages} style={paginatorButtonStyle(portalPage >= totalPortalPages)}>Next</button>
                    </div>
                </div>

                <div style={scrollContainerStyle} className="custom-scrollbar">
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                        <thead>
                            <tr>
                                <th style={{ ...tableHeaderStyle, textAlign: "left" }}>Portal Path</th>
                                <th style={{ ...tableHeaderStyle, color: "#c53030" }}>VIP</th>
                                <th style={{ ...tableHeaderStyle, color: "#c53030" }}>Rush</th>
                                <th style={{ ...tableHeaderStyle, color: "#dd6b20" }}>Due Date</th>
                                <th style={{ ...tableHeaderStyle, color: "#e53e3e" }}>Past Due</th>
                                <th style={{ ...tableHeaderStyle, color: "#f70303" }}>Late</th>
                                <th style={tableHeaderStyle}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedPortals.map((p, idx) => {
                                const rowPriorityTotal = Number(p.vip) + Number(p.rush) + Number(p.due) + Number(p.past) + Number(p.late);
                                return (
                                    <tr key={idx}>
                                        <td style={{ ...cellStyle, textAlign: "left", fontWeight: "800", color: "#4a5568" }}>{p.path}</td>
                                        <td style={{ ...cellStyle, fontWeight: "900", color: p.vip > 0 ? "#c53030" : "#cbd5e1" }}>{p.vip}</td>
                                        <td style={{ ...cellStyle, fontWeight: "900", color: p.rush > 0 ? "#c53030" : "#cbd5e1" }}>{p.rush}</td>
                                        <td style={{ ...cellStyle, fontWeight: "900", color: p.due > 0 ? "#dd6b20" : "#cbd5e1" }}>{p.due}</td>
                                        <td style={{ ...cellStyle, fontWeight: "900", color: p.past > 0 ? "#e53e3e" : "#cbd5e1" }}>{p.past}</td>
                                        <td style={{ ...cellStyle, fontWeight: "900", color: p.late > 0 ? "#f70303" : "#cbd5e1" }}>{p.late}</td>
                                        <td style={{ ...cellStyle, backgroundColor: "#f8fafc", fontWeight: "900" }}>{rowPriorityTotal}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15,23,42,0.06)" }}>
                <h4 style={{ margin: "0 0 16px 0", fontSize: "15px", color: "#0f172a", fontWeight: "900" }}>
                    ⏱️ Portal SLA Audit Table
                </h4>

                <div style={scrollContainerStyle} className="custom-scrollbar">
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                        <thead>
                            <tr>
                                <th style={{ ...tableHeaderStyle, textAlign: "left" }}>Portal Workspace</th>
                                <th style={{ ...tableHeaderStyle, textAlign: "center" }}>Volume</th>
                                <th style={{ ...tableHeaderStyle, textAlign: "center", color: "#16a34a" }}>Within SLA</th>
                                <th style={{ ...tableHeaderStyle, textAlign: "center", color: "#dc2626" }}>Over SLA</th>
                                <th style={{ ...tableHeaderStyle, textAlign: "center" }}>SLA Hit Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {portalBreakdown.map((portal, idx) => {
                                const totalPortalSla = Number(portal.within || 0) + Number(portal.over || 0);
                                const portalSlaHitRatePct = totalPortalSla > 0 ? Math.round((Number(portal.within || 0) / totalPortalSla) * 100) : 100;

                                return (
                                    <tr key={`${portal.path}-${idx}`}>
                                        <td style={{ ...cellStyle, textAlign: "left", fontWeight: "800", color: "#334155" }}>{portal.path}</td>
                                        <td style={{ ...cellStyle, fontWeight: "900", color: "#0f172a" }}>{portal.total}</td>
                                        <td style={{ ...cellStyle, color: "#16a34a", fontWeight: "900" }}>{portal.within}</td>
                                        <td style={{ ...cellStyle, color: "#dc2626", fontWeight: "900" }}>{portal.over}</td>
                                        <td style={{ ...cellStyle, verticalAlign: "middle" }}>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                                <div style={{ width: "120px", height: "8px", backgroundColor: "#fee2e2", borderRadius: "4px", overflow: "hidden", display: "flex" }}>
                                                    <div style={{ width: `${portalSlaHitRatePct}%`, backgroundColor: "#22c55e", height: "100%" }} />
                                                </div>
                                                <span style={{ fontWeight: "900", color: portalSlaHitRatePct >= 85 ? "#16a34a" : "#d97706", minWidth: "35px", textAlign: "right" }}>
                                                    {portalSlaHitRatePct}%
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}

                            {portalBreakdown.length === 0 && (
                                <tr>
                                    <td colSpan="5" style={{ padding: "32px", textAlign: "center", color: "#94a3b8" }}>
                                        No SLA data available for selected filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="dashboard-summary-grid">

                <div style={{ minWidth: 0, backgroundColor: "white", borderRadius: "10px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", flexWrap: "wrap", gap: "10px" }}>
                        <h3 style={{ fontSize: "16px", color: "#333", fontWeight: "900", margin: 0 }}>👥 Live Reviewer Summary</h3>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <button onClick={() => setEmpPage(p => Math.max(1, p - 1))} disabled={empPage === 1} style={paginatorButtonStyle(empPage === 1)}>Prev</button>
                            <span style={{ fontSize: "12px", fontWeight: "900", alignSelf: "center" }}>Page {empPage} of {Math.max(1, totalEmpPages)}</span>
                            <button onClick={() => setEmpPage(p => Math.min(totalEmpPages, p + 1))} disabled={empPage >= totalEmpPages} style={paginatorButtonStyle(empPage >= totalEmpPages)}>Next</button>
                        </div>
                    </div>

                    <div style={{ overflowX: "auto", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead>
                                <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                                    <th style={{ padding: "12px 16px", fontWeight: "600", color: "#475569" }}>Reviewer Name</th>
                                    <th style={{ padding: "12px 16px", fontWeight: "600", color: "#475569", textAlign: "center" }}>Assigned</th>
                                    <th style={{ padding: "12px 16px", fontWeight: "600", color: "#475569", textAlign: "center" }}>Oldest Waiting</th>
                                    <th style={{ padding: "12px 16px", fontWeight: "600", color: "#475569", textAlign: "center" }}>Working</th>
                                    <th style={{ padding: "12px 16px", fontWeight: "600", color: "#475569", textAlign: "center" }}>Completed</th>
                                    <th style={{ padding: "12px 16px", fontWeight: "600", color: "#475569", textAlign: "center" }}>Rejected</th>
                                    <th style={{ padding: "12px 16px", fontWeight: "600", color: "#7c3aed", textAlign: "center" }}>Exceptions</th>
                                    <th style={{ padding: "12px 16px", fontWeight: "600", color: "#475569" }}>Done Files By Portal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedEmployees.map((emp, idx) => {
                                    const portalEntries = emp.portals ? Object.entries(emp.portals) : [];
                                    const isExpanded = !!expandedReviewers[emp.name];
                                    const visiblePortals = isExpanded ? portalEntries : portalEntries.slice(0, 3);
                                    const hasMore = portalEntries.length > 3;

                                    const isFilesExpanded = !!expandedActiveFiles[emp.name];
                                    const activeFilesList = emp.active_files || [];
                                    const hasAssignedFiles = Number(emp.assigned || 0) >= 1;

                                    return (
                                        <React.Fragment key={idx}>
                                            <tr style={{ borderBottom: isFilesExpanded && hasAssignedFiles ? "none" : "1px solid #f1f5f9" }}>
                                                <td style={{ padding: "14px 16px", fontWeight: "600", color: "#1e293b", verticalAlign: "middle" }}>{emp.name}</td>
                                                <td style={{ padding: "14px 16px", textAlign: "center", verticalAlign: "middle" }}>
                                                    {hasAssignedFiles ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleActiveFilesExpand(emp.name)}
                                                            title={isFilesExpanded ? "Collapse files" : `Show all ${activeFilesList.length || emp.assigned} files`}
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                gap: "5px",
                                                                backgroundColor: isFilesExpanded ? "#2563eb" : "#eff6ff",
                                                                color: isFilesExpanded ? "#ffffff" : "#1d4ed8",
                                                                border: isFilesExpanded ? "1px solid #1d4ed8" : "1px solid #bfdbfe",
                                                                borderRadius: "6px",
                                                                padding: "3px 8px",
                                                                cursor: "pointer",
                                                                fontWeight: "900",
                                                                fontSize: "13px",
                                                                transition: "all 0.15s ease",
                                                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                                                            }}
                                                        >
                                                            <span>{emp.assigned}</span>
                                                            <span style={{ fontSize: "11px", fontWeight: "900" }}>{isFilesExpanded ? "−" : "+"}</span>
                                                        </button>
                                                    ) : (
                                                        <span style={{ color: "#3182ce", fontWeight: "900" }}>{emp.assigned}</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: "14px 16px", textAlign: "center", verticalAlign: "middle" }}>
                                                    {(() => {
                                                        const hasActiveFiles = (emp.assigned > 0 || emp.working > 0);
                                                        if (!hasActiveFiles || !emp.oldest_assign_time) {
                                                            return <span style={{ color: "#94a3b8", fontWeight: "600" }}>-</span>;
                                                        }
                                                        const waitText = formatWaitDuration(emp.oldest_assign_time);
                                                        if (!waitText) return <span style={{ color: "#94a3b8", fontWeight: "600" }}>-</span>;

                                                        const parsed = parseCustomDate(emp.oldest_assign_time) || new Date(emp.oldest_assign_time);
                                                        const diffHours = parsed ? (Date.now() - parsed.getTime()) / (1000 * 60 * 60) : 0;

                                                        let bg = "#eff6ff";
                                                        let color = "#1d4ed8";
                                                        let border = "#bfdbfe";
                                                        if (diffHours >= 2) {
                                                            bg = "#fee2e2";
                                                            color = "#b91c1c";
                                                            border = "#fecaca";
                                                        } else if (diffHours >= 1) {
                                                            bg = "#fef3c7";
                                                            color = "#b45309";
                                                            border = "#fde68a";
                                                        }

                                                        const fullDateTimeStr = formatDateTime(emp.oldest_assign_time) || emp.oldest_assign_time;

                                                        return (
                                                            <span
                                                                style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    gap: "4px",
                                                                    backgroundColor: bg,
                                                                    color: color,
                                                                    border: `1px solid ${border}`,
                                                                    borderRadius: "6px",
                                                                    padding: "3px 8px",
                                                                    fontSize: "11px",
                                                                    fontWeight: "800",
                                                                    whiteSpace: "nowrap"
                                                                }}
                                                                title={`Oldest file assigned at: ${fullDateTimeStr}`}
                                                            >
                                                                ⏱️ {waitText}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td style={{ padding: "14px 16px", textAlign: "center", color: "#d69e2e", fontWeight: "900", verticalAlign: "middle" }}>{emp.working}</td>
                                                <td style={{ padding: "14px 16px", textAlign: "center", color: "#2f855a", fontWeight: "900", verticalAlign: "middle" }}>{emp.completed}</td>
                                                <td style={{ padding: "14px 16px", textAlign: "center", color: "#e53e3e", fontWeight: "900", verticalAlign: "middle" }}>{emp.rejected}</td>
                                                <td style={{ padding: "14px 16px", textAlign: "center", color: emp.exceptions > 0 ? "#7c3aed" : "#94a3b8", fontWeight: "900", verticalAlign: "middle" }}>{emp.exceptions || 0}</td>
                                                <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                                                    {portalEntries.length > 0 ? (
                                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                                                            {visiblePortals.map(([portal, count]) => (
                                                                <span
                                                                    key={portal}
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        gap: "6px",
                                                                        backgroundColor: "#f8fafc",
                                                                        border: "1px solid #e2e8f0",
                                                                        borderRadius: "6px",
                                                                        padding: "3px 8px",
                                                                        fontSize: "11px",
                                                                        boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
                                                                    }}
                                                                >
                                                                    <span style={{ fontWeight: "800", color: "#16a34a", backgroundColor: "#dcfce7", padding: "1px 6px", borderRadius: "4px" }}>
                                                                        {count}
                                                                    </span>
                                                                    <span style={{ fontWeight: "600", color: "#334155" }}>
                                                                        {portal}
                                                                    </span>
                                                                </span>
                                                            ))}
                                                            {hasMore && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleReviewerExpand(emp.name)}
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        gap: "4px",
                                                                        backgroundColor: isExpanded ? "#fee2e2" : "#eff6ff",
                                                                        color: isExpanded ? "#b91c1c" : "#2563eb",
                                                                        border: isExpanded ? "1px solid #fecaca" : "1px solid #bfdbfe",
                                                                        borderRadius: "6px",
                                                                        padding: "3px 8px",
                                                                        fontSize: "11px",
                                                                        fontWeight: "700",
                                                                        cursor: "pointer",
                                                                        transition: "all 0.15s ease"
                                                                    }}
                                                                    title={isExpanded ? "Collapse portal list" : "Expand to view all portals"}
                                                                >
                                                                    {isExpanded ? "Show Less ▲" : `+${portalEntries.length - 3} more ▼`}
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: "#94a3b8", fontStyle: "italic", fontSize: "12px" }}>No activity logs found</span>
                                                    )}
                                                </td>
                                            </tr>
                                            {isFilesExpanded && hasAssignedFiles && (
                                                <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                                    <td colSpan={8} style={{ padding: "8px 16px 12px 16px" }}>
                                                        <div style={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            alignItems: "center",
                                                            gap: "10px",
                                                            backgroundColor: "#ffffff",
                                                            border: "1px solid #e2e8f0",
                                                            borderRadius: "6px",
                                                            padding: "8px 14px",
                                                            boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
                                                        }}>
                                                            <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                                                Files ({activeFilesList.length || emp.assigned}):
                                                            </span>
                                                            {activeFilesList.length > 0 ? (
                                                                activeFilesList.map((file, fIdx) => {
                                                                    const dur = formatWaitDuration(file.assign_time) || "Just now";
                                                                    return (
                                                                        <span
                                                                            key={fIdx}
                                                                            style={{
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                gap: "6px",
                                                                                backgroundColor: "#f8fafc",
                                                                                border: "1px solid #cbd5e1",
                                                                                borderRadius: "6px",
                                                                                padding: "4px 10px",
                                                                                fontSize: "12px"
                                                                            }}
                                                                        >
                                                                            <span style={{ fontWeight: "700", color: "#1e293b" }}>
                                                                                {file.client_name || "Unknown"}
                                                                            </span>
                                                                            <span style={{
                                                                                fontWeight: "800",
                                                                                color: "#1d4ed8",
                                                                                backgroundColor: "#eff6ff",
                                                                                border: "1px solid #bfdbfe",
                                                                                borderRadius: "4px",
                                                                                padding: "1px 6px",
                                                                                fontSize: "11px"
                                                                            }}>
                                                                                ⏱️ {dur}
                                                                            </span>
                                                                        </span>
                                                                    );
                                                                })
                                                            ) : (
                                                                <span
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        gap: "6px",
                                                                        backgroundColor: "#f8fafc",
                                                                        border: "1px solid #cbd5e1",
                                                                        borderRadius: "6px",
                                                                        padding: "4px 10px",
                                                                        fontSize: "12px"
                                                                    }}
                                                                >
                                                                    <span style={{ fontWeight: "700", color: "#1e293b" }}>
                                                                        Assigned
                                                                    </span>
                                                                    <span style={{
                                                                        fontWeight: "800",
                                                                        color: "#1d4ed8",
                                                                        backgroundColor: "#eff6ff",
                                                                        border: "1px solid #bfdbfe",
                                                                        borderRadius: "4px",
                                                                        padding: "1px 6px",
                                                                        fontSize: "11px"
                                                                    }}>
                                                                        ⏱️ {formatWaitDuration(emp.oldest_assign_time) || "Just now"}
                                                                    </span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr style={{ backgroundColor: "#f8fafc", borderTop: "2px solid #cbd5e1" }}>
                                    <td style={{ padding: "12px 16px", fontWeight: "800", color: "#0f172a" }}>Total</td>
                                    <td style={{ padding: "12px 16px", textAlign: "center", fontWeight: "900", color: "#3182ce" }}>
                                        {employeeData.reduce((acc, e) => acc + (Number(e.assigned) || 0), 0)}
                                    </td>
                                    <td style={{ padding: "12px 16px", textAlign: "center", color: "#94a3b8", fontWeight: "700" }}>-</td>
                                    <td style={{ padding: "12px 16px", textAlign: "center", fontWeight: "900", color: "#d69e2e" }}>
                                        {employeeData.reduce((acc, e) => acc + (Number(e.working) || 0), 0)}
                                    </td>
                                    <td style={{ padding: "12px 16px", textAlign: "center", fontWeight: "900", color: "#2f855a" }}>
                                        {employeeData.reduce((acc, e) => acc + (Number(e.completed) || 0), 0)}
                                    </td>
                                    <td style={{ padding: "12px 16px", textAlign: "center", fontWeight: "900", color: "#e53e3e" }}>
                                        {employeeData.reduce((acc, e) => acc + (Number(e.rejected) || 0), 0)}
                                    </td>
                                    <td style={{ padding: "12px 16px", textAlign: "center", fontWeight: "900", color: "#7c3aed" }}>
                                        {employeeData.reduce((acc, e) => acc + (Number(e.exceptions) || 0), 0)}
                                    </td>
                                    <td style={{ padding: "12px 16px", color: "#64748b", fontWeight: "700", fontSize: "12px" }}>
                                        {employeeData.reduce((acc, e) => acc + (Number(e.completed) || 0) + (Number(e.rejected) || 0) + (Number(e.exceptions) || 0), 0)} closed files
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <div style={{ minWidth: 0, backgroundColor: "white", borderRadius: "10px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", height: "520px" }}>
                    <h3 style={{ marginBottom: "15px", fontSize: "16px", color: "#333", fontWeight: "900" }}>📊 Client Breakdown</h3>
                    <div style={{ flex: 1, overflow: "auto", borderRadius: "8px", border: "1px solid #e2e8f0" }} className="custom-scrollbar">
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead>
                                <tr>
                                    <th style={{ ...tableHeaderStyle, textAlign: "left" }}>Client / Dept</th>
                                    <th style={tableHeaderStyle}>Total</th>
                                    <th style={tableHeaderStyle}>Unassign</th>
                                    <th style={tableHeaderStyle}>In Progress</th>
                                    <th style={tableHeaderStyle}>Completed</th>
                                    <th style={tableHeaderStyle}>Rejected</th>
                                    <th style={{ ...tableHeaderStyle, color: "#7c3aed" }}>Exceptions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clientBreakdownData.map((parent) => (
                                    <React.Fragment key={parent.name}>
                                        <tr style={{ backgroundColor: "#f7fafc" }}>
                                            <td colSpan="7" style={{ padding: "8px 10px", fontWeight: "900" }}>{parent.name}</td>
                                        </tr>
                                        {[
                                            { label: "QC", data: parent.qc },
                                            { label: "Revised", data: parent.rev }
                                        ].map(row => (
                                            <tr key={row.label}>
                                                <td style={{ ...cellStyle, textAlign: "left", paddingLeft: "25px", color: "#4a5568" }}>↳ {row.label}</td>
                                                <td style={{ ...cellStyle, fontWeight: "900" }}>{row.data.total}</td>
                                                <td style={{ ...cellStyle, color: "#3182ce" }}>{row.data.unassign}</td>
                                                <td style={{ ...cellStyle, color: "#d69e2e" }}>{row.data.working}</td>
                                                <td style={{ ...cellStyle, color: "#2f855a" }}>{row.data.completed}</td>
                                                <td style={{ ...cellStyle, color: "#e53e3e" }}>{row.data.rejected}</td>
                                                <td style={{ ...cellStyle, color: row.data.exceptions > 0 ? "#7c3aed" : "#94a3b8", fontWeight: "900" }}>{row.data.exceptions || 0}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <style>
                {`
                    .dashboard-summary-grid {
                        display: grid;
                        grid-template-columns: minmax(0, 1.3fr) minmax(0, 0.7fr);
                        gap: 20px;
                        margin-top: 24px;
                    }
                    @media (max-width: 1400px) {
                        .dashboard-summary-grid {
                            grid-template-columns: 1fr;
                        }
                    }
                    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
                    .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                    .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #a0aec0; }
                `}
            </style>
        </div>
    );
};

export default StatsOverview;
